import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import constate from "constate";
import { useHistory, useLocation } from "react-router-dom";

import { SettingsContext } from "../../contexts";

/** `?lookbook=<id>` — open the overlay on an existing lookbook, to edit it. */
export const LOOKBOOK_QUERY_PARAM = "lookbook";

/** `?newLookbook=1` — open the overlay empty, from the Lookbooks tab. */
export const NEW_LOOKBOOK_QUERY_PARAM = "newLookbook";

/** The two dialogs PROCEED walks, in order. */
export const STEP_AMOUNTS = "amounts";
export const STEP_COVER = "cover";

/**
 * The money lines a PDF may print, per request type. These keys are persisted
 * in `lookbooks.data.copies` and read by LookbookService — renaming one
 * silently turns that toggle back ON for every existing lookbook.
 */
export const LINE_BUY_BUDGET = "order_buy_budget";
export const LINE_SELL_TARGET = "order_sell_target";
export const LINE_EXPECTED_PRICE = "sell_expected_price";
export const LINE_LISTING_PRICE = "sell_listing_price";

/**
 * Which request column each line reads, and what the card calls it. An order
 * request's SELL TARGET is `max_budget` and a sell request's EXPECTED PRICE is
 * `price_expectation` — both pre-existing columns the rest of the app already
 * uses. `buy_budget` and `listing_price` are the two new ones.
 */
export const LINE_FIELDS = {
  order: [
    { key: LINE_BUY_BUDGET, field: "buy_budget", label: "Buy budget" },
    { key: LINE_SELL_TARGET, field: "max_budget", label: "Sell target" },
  ],
  sell: [
    { key: LINE_EXPECTED_PRICE, field: "price_expectation", label: "Expected price" },
    { key: LINE_LISTING_PRICE, field: "listing_price", label: "Listing price" },
  ],
};

/**
 * The admin copy prints everything. The network copy (`external` — the key is
 * persisted, only the label changed) starts with the two SELLING figures off:
 * it goes to dealers, who should see what we will pay, not what we sell at.
 */
const DEFAULT_COPIES = {
  admin: {
    [LINE_BUY_BUDGET]: true,
    [LINE_SELL_TARGET]: true,
    [LINE_EXPECTED_PRICE]: true,
    [LINE_LISTING_PRICE]: true,
  },
  external: {
    [LINE_BUY_BUDGET]: true,
    [LINE_SELL_TARGET]: false,
    [LINE_EXPECTED_PRICE]: true,
    [LINE_LISTING_PRICE]: false,
  },
};

/** An order request's card fields, or a sell request's. */
export const fieldsFor = (request) =>
  request?.request_type === "sell" ? LINE_FIELDS.sell : LINE_FIELDS.order;

/**
 * Selection state for a request lookbook.
 *
 * The overlay deliberately does NOT own a list of its own: it layers on top of
 * the Orders page and reuses that page's filters, search and infinite list, so
 * everything a user can already find there can go into a lookbook. What lives
 * here is only what the page cannot express — which requests are picked, in
 * what order, and the cover.
 *
 * Editing an existing lookbook is the same overlay, opened with
 * `?lookbook=<id>` and hydrated from the API.
 */
const [RequestLookbookProvider, useRequestLookbook] = constate(() => {
  const history = useHistory();
  const location = useLocation();
  const { iframeQueryParams } = useContext(SettingsContext);

  const editingId = useMemo(
    () => new URLSearchParams(location.search).get(LOOKBOOK_QUERY_PARAM),
    [location.search]
  );
  const startRequested = useMemo(
    () => new URLSearchParams(location.search).has(NEW_LOOKBOOK_QUERY_PARAM),
    [location.search]
  );

  const [active, setActive] = useState(false);
  // id -> request object. Keyed by id so a re-selected request keeps its place
  // in `order` rather than jumping to the end.
  const [selected, setSelected] = useState({});
  const [order, setOrder] = useState([]);
  // PROCEED walks two dialogs: the money amounts per request, then the cover.
  // One `step` rather than two booleans — they are mutually exclusive, and two
  // flags let both open at once when a back-navigation raced a close.
  const [step, setStep] = useState(null);
  const [copies, setCopies] = useState(DEFAULT_COPIES);
  // Per-request save state for the amounts dialog: id -> "saving" | "saved" | error.
  const [saveState, setSaveState] = useState({});
  // Suggested amounts from the recommendations endpoint, shown in EMPTY fields:
  // id -> { [field]: minor }. Nothing is written to the request until PROCEED
  // (applyPrefills), so opening and closing the dialog changes no data. A
  // field the rep has saved — or cleared — is `touched` and never refilled.
  const [prefills, setPrefills] = useState({});
  const [touched, setTouched] = useState({});

  const [template, setTemplate] = useState("dao");
  const [lookbookName, setLookbookName] = useState("");
  const [lookbookDate, setLookbookDate] = useState("");
  const [client, setClient] = useState(null);

  const [hydrating, setHydrating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Write one request's money fields.
   *
   * Optimistic: the typed value lands in `selected` immediately so the input
   * stays responsive and the PDF preview text is right, then the save follows.
   * On failure the field is left as typed and the card shows the error rather
   * than silently reverting under the cursor.
   *
   * Goes to the NARROW endpoint. `PUT /api/requests/{id}` does not patch — it
   * rebuilds the record and nulls whatever the payload omits, which is how
   * `year` and `price_expectation` were being wiped in production for weeks.
   */
  const saveAmounts = useCallback(async (requestId, patch) => {
    const fields = Object.keys(patch).filter((k) => k !== "currency");
    if (fields.length) {
      setTouched((previous) => ({
        ...previous,
        [requestId]: {
          ...previous[requestId],
          ...Object.fromEntries(fields.map((f) => [f, true])),
        },
      }));
    }
    setSelected((previous) =>
      previous[requestId]
        ? { ...previous, [requestId]: { ...previous[requestId], ...patch } }
        : previous
    );
    setSaveState((previous) => ({ ...previous, [requestId]: "saving" }));

    try {
      const { data } = await axios.post(
        `/api/requests/${requestId}/lookbook-amounts`,
        patch
      );

      // Take the server's normalised values back — it turns "" and "0" into
      // null, and the card's "is there a figure?" check has to agree with the
      // PDF's, or the two disagree about whether a line will print.
      setSelected((previous) =>
        previous[requestId]
          ? {
              ...previous,
              [requestId]: {
                ...previous[requestId],
                price_expectation: data.price_expectation,
                max_budget: data.max_budget,
                buy_budget: data.buy_budget,
                listing_price: data.listing_price,
                currency: data.currency,
              },
            }
          : previous
      );
      setSaveState((previous) => ({ ...previous, [requestId]: "saved" }));

      return data;
    } catch (e) {
      setSaveState((previous) => ({ ...previous, [requestId]: "error" }));

      return null;
    }
  }, []);

  /** Replace one request's suggestions; fields already touched are skipped. */
  const setPrefill = useCallback((requestId, values) => {
    setPrefills((previous) => ({ ...previous, [requestId]: values }));
  }, []);

  /**
   * The suggestions still sitting in empty, untouched fields, as save patches.
   * Read by the card (to show them) and by applyPrefills (to save them).
   */
  const pendingPrefill = useCallback(
    (requestId) => {
      const request = selected[requestId];
      const values = prefills[requestId] ?? {};

      return Object.fromEntries(
        Object.entries(values).filter(
          ([field, minor]) =>
            minor &&
            !touched[requestId]?.[field] &&
            (request?.[field] === null ||
              request?.[field] === undefined ||
              request?.[field] === "" ||
              Number(request?.[field]) === 0)
        ).map(([field, minor]) => [field, String(minor)])
      );
    },
    [prefills, selected, touched]
  );

  /** PROCEED: save every suggestion still showing, so the PDF prints it. */
  const applyPrefills = useCallback(async () => {
    await Promise.all(
      order
        .map((id) => [id, pendingPrefill(id)])
        .filter(([, patch]) => Object.keys(patch).length > 0)
        .map(([id, patch]) => saveAmounts(id, patch))
    );
  }, [order, pendingPrefill, saveAmounts]);

  const reset = useCallback(() => {
    setSelected({});
    setOrder([]);
    setStep(null);
    setCopies(DEFAULT_COPIES);
    setSaveState({});
    setPrefills({});
    setTouched({});
    setTemplate("dao");
    setLookbookName("");
    setLookbookDate("");
    setClient(null);
    setError(null);
  }, []);

  // `?lookbook=<id>` is the edit entry point — the Lookbooks tab pushes it.
  useEffect(() => {
    if (!editingId) return undefined;

    let alive = true;
    setActive(true);
    setHydrating(true);
    setError(null);

    (async () => {
      try {
        const { data } = await axios.get(`/api/lookbooks/${editingId}`);
        if (!alive) return;

        const requests = data?.data?.request_objects ?? [];
        setSelected(
          requests.reduce((acc, request) => ({ ...acc, [request.id]: request }), {})
        );
        // Ids are numbers from the API but strings once they round-trip through
        // Object.keys — normalise to strings everywhere, or the merge effect
        // below re-appends every request and duplicates the React keys.
        setOrder((data?.data?.requests ?? []).map(String));
        setTemplate(data?.data?.template ?? "dao");
        setLookbookName(data?.data?.lookbookName ?? "");
        setLookbookDate(data?.data?.lookbookDate ?? "");
        setClient(data?.data?.client ?? null);
        // A lookbook saved before the toggles existed has no `copies`; it means
        // "everything printed", which is exactly the default.
        setCopies({
          admin: { ...DEFAULT_COPIES.admin, ...(data?.data?.copies?.admin ?? {}) },
          external: { ...DEFAULT_COPIES.external, ...(data?.data?.copies?.external ?? {}) },
        });
      } catch (e) {
        if (alive) setError("Couldn’t load that lookbook. Please try again.");
      } finally {
        if (alive) setHydrating(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [editingId]);

  // Keep the order in step with the selection: newly picked requests go to the
  // top (that is the point — a selected request "moves to the top"), dropped
  // ones fall out.
  useEffect(() => {
    setOrder((previous) => {
      const known = Object.keys(selected);
      const kept = (previous ?? []).map(String).filter((id) => selected[id]);
      const added = known.filter((id) => !kept.includes(id));

      return [...added, ...kept];
    });
  }, [selected]);

  const stripOverlayParams = useCallback(() => {
    const params = new URLSearchParams(location.search);

    if (
      !params.has(LOOKBOOK_QUERY_PARAM) &&
      !params.has(NEW_LOOKBOOK_QUERY_PARAM)
    ) {
      return;
    }

    params.delete(LOOKBOOK_QUERY_PARAM);
    params.delete(NEW_LOOKBOOK_QUERY_PARAM);
    history.replace({
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : "",
    });
  }, [history, location.pathname, location.search]);

  const start = useCallback(() => {
    reset();
    setActive(true);
  }, [reset]);

  // Arriving from the Lookbooks tab's "+ New Lookbook". The param is consumed
  // immediately so a refresh, or cancelling and coming back, does not reopen it.
  useEffect(() => {
    if (!startRequested) return;

    start();
    stripOverlayParams();
  }, [startRequested, start, stripOverlayParams]);

  const cancel = useCallback(() => {
    setActive(false);
    reset();
    stripOverlayParams();
  }, [reset, stripOverlayParams]);

  const isSelected = useCallback((id) => !!selected[id], [selected]);

  const toggle = useCallback((request) => {
    setSelected((previous) => {
      const next = { ...previous };

      if (next[request.id]) {
        delete next[request.id];
      } else {
        next[request.id] = request;
      }

      return next;
    });
  }, []);

  const remove = useCallback((id) => {
    setSelected((previous) => {
      const next = { ...previous };
      delete next[id];

      return next;
    });
  }, []);

  const canProceed = order.length > 0;
  const canGenerate = canProceed && !!client;

  const generate = useCallback(async () => {
    if (!canGenerate) return;

    setSubmitting(true);
    setError(null);

    const payload = {
      type: "request",
      assignedTo: client.id,
      template,
      lookbookName,
      lookbookDate,
      requests: order.map(Number),
      copies,
    };

    try {
      if (editingId) {
        await axios.put(`/api/lookbooks/${editingId}`, payload);
      } else {
        await axios.post("/api/lookbooks", payload);
      }

      setActive(false);
      reset();
      history.push(
        `/requests/lookbooks${iframeQueryParams ? `?${iframeQueryParams}` : ""}`
      );
    } catch (e) {
      setError("Couldn’t generate the lookbook. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [
    canGenerate,
    client,
    // Not just lint: without this, `generate` keeps the toggle state from the
    // render it was created in, so flipping a toggle and pressing Generate
    // straight away would post the PREVIOUS visibility set.
    copies,
    editingId,
    history,
    iframeQueryParams,
    lookbookDate,
    lookbookName,
    order,
    reset,
    template,
  ]);

  return {
    active,
    isEditing: !!editingId,
    hydrating,
    submitting,
    error,
    setError,
    start,
    cancel,
    selected,
    order,
    setOrder,
    count: order.length,
    isSelected,
    toggle,
    remove,
    // PROCEED on the dock opens the amounts dialog; PROCEED there opens the
    // cover. Closing either leaves the overlay itself open, so a mis-click
    // never costs the selection.
    step,
    amountsOpen: step === STEP_AMOUNTS,
    coverOpen: step === STEP_COVER,
    openAmounts: () => setStep(STEP_AMOUNTS),
    openCover: () => setStep(STEP_COVER),
    closeStep: () => setStep(null),
    backToAmounts: () => setStep(STEP_AMOUNTS),
    copies,
    toggleCopyLine: (copy, key) =>
      setCopies((previous) => ({
        ...previous,
        [copy]: { ...previous[copy], [key]: !previous[copy]?.[key] },
      })),
    saveAmounts,
    saveState,
    setPrefill,
    pendingPrefill,
    applyPrefills,
    template,
    setTemplate,
    lookbookName,
    setLookbookName,
    lookbookDate,
    setLookbookDate,
    client,
    setClient,
    canProceed,
    canGenerate,
    generate,
  };
});

export { RequestLookbookProvider, useRequestLookbook };
